import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';
import { ofType } from '@ngrx/effects';
import { ActionsSubject, Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { esignSelectors, esignActions } from 'src/app/root-store/esign-store';
import { RequiredDocument } from '../models/required-document.model';
import { TemplateSearchParameters } from '../models/template-search-parameters.model';
import { Template, SelectedTemplate } from '../models/template.model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FeatureFlag } from 'src/app/shared/models/enums/feature-flag';
import { ActivatedRoute } from '@angular/router';
import { isEmpty } from 'src/app/shared/utilities/obj-utilities';

@UntilDestroy()
@Component({
    selector: 'nova-template-mapping-fly-out',
    templateUrl: './template-mapping-fly-out.component.html',
    styleUrls: ['./template-mapping-fly-out.component.scss']
})
export class TemplateMappingFlyOutComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild('flyOut') flyOut: MatSidenav;
    @Input() contractId: number;
    @Output() templateSelection = new EventEmitter<SelectedTemplate>();

    selectedDoc: RequiredDocument;
    selectedTemplate: Template;
    canRemoveMapping$: Observable<boolean>;
    featureFlag = FeatureFlag;
    isRequirementsPage: boolean = false;
    private _selectedFolderId: string;

    private readonly _cleanup$ = new Subject();

    constructor(
        private readonly _store: Store,
        private readonly actionListener$: ActionsSubject,
        private readonly route: ActivatedRoute
    ) {}

    ngAfterViewInit() {
        this._store
            .select(esignSelectors.selectSelectedDocumentType)
            .pipe(
                filter((data) => !isEmpty(data)),
                untilDestroyed(this)
            )
            .subscribe((doc) => {
                this.flyOut?.open();
                this.selectedDoc = doc;
            });

        this._store
            .select<string>(esignSelectors.selectSelectedFolder)
            .pipe(untilDestroyed(this))
            .subscribe((folderId) => {
                this._selectedFolderId = folderId;
                if (folderId) {
                    this.search();
                }
            });
    }

    ngOnInit(): void {
        this.canRemoveMapping$ = this._store.select(esignSelectors.selectTemplateMappingAllowedRTCR);

        this.actionListener$
            .pipe(ofType(esignActions.EsignActions.associateTemplateDocumentTypeFailure), untilDestroyed(this))
            .subscribe(() => {
                this.cancel();
            });

        this.isRequirementsPage = this.route.snapshot.url[1]?.path === 'requirement';
    }

    ngOnDestroy(): void {
        this._cleanup$.next(true);
        this._cleanup$.complete();
    }

    templateSelected(template: Template): void {
        this.selectedTemplate = template;
    }

    associateTemplate(): void {
        const selection: SelectedTemplate = {
            contractId: this.contractId,
            documentTypeId: this.selectedDoc.documentTypeId,
            template: this.selectedTemplate,
            isAdHoc: this.selectedDoc.isAdHoc
        };

        this.templateSelection.emit(selection);
        this.cancel();
    }

    searchTemplates(params: TemplateSearchParameters) {
        this.search(params.searchText, params.searchAllFolders);
    }

    search(text: string = null, searchAllFolders: boolean = false): void {
        if (searchAllFolders) {
            this._store.dispatch(esignActions.EsignActions.clearSelectedFolder());
        }
        this._store.dispatch(
            esignActions.EsignActions.searchTemplates({
                folderId: searchAllFolders ? null : this._selectedFolderId,
                name: text
            })
        );
        this._store.dispatch(esignActions.EsignActions.setTemplateSearchComplete({ isTemplateSearchComplete: true }));
    }

    cancel(): void {
        this._store.dispatch(esignActions.EsignActions.clearTemplateMappingState());
        this.selectedTemplate = null;
        this.flyOut.toggle();
    }
}
